export type FontStyles = {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
};

export type TruncationResult = {
  needsTruncation: boolean;
  truncatedText: string;
};

/**
 * Calculates if and where to truncate text to fit within a given number of lines.
 *
 * @param text The full text string.
 * @param maxLines The maximum number of lines allowed.
 * @param containerWidth The width of the container in pixels.
 * @param styles The font styles affecting the text rendering.
 * @returns An object containing a boolean indicating if truncation is needed,
 *          and the truncated text if applicable.
 */
export const calculateTrimming = (
  text: string,
  maxLines: number,
  containerWidth: number,
  styles: FontStyles,
  charLimit: number = 100,
): TruncationResult => {
  // Create a temporary DOM element to measure text dimensions
  const tempElement = document.createElement('div');
  tempElement.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${containerWidth}px;
    font-family: ${styles.fontFamily};
    font-size: ${styles.fontSize};
    line-height: ${styles.lineHeight};
    word-break: break-word;
    white-space: normal;
  `;
  document.body.appendChild(tempElement);

  // Calculate the exact line height
  tempElement.innerHTML = 'A';
  const lineHeight = tempElement.offsetHeight;
  const maxHeight = lineHeight * maxLines;

  // Test if the full text fits
  tempElement.innerHTML = text;
  if (tempElement.offsetHeight <= maxHeight) {
    document.body.removeChild(tempElement);
    // If text fits within height, only show truncation UI if text exceeds charLimit
    return {
      needsTruncation: text.length > charLimit,
      truncatedText: text,
    };
  }

  // Binary search for the best fit
  let start = 0;
  let end = text.length;
  let bestFit = '';

  while (start <= end) {
    const mid = Math.floor((start + end) / 2);
    const testText = text.substring(0, mid);

    // Test with "... View more" button included in the measurement
    tempElement.innerHTML =
      testText +
      '... <button style="display: inline; font: inherit; text-decoration: underline; color: #1890ff;">View more</button>';

    if (tempElement.offsetHeight <= maxHeight) {
      bestFit = testText;
      start = mid + 1;
    } else {
      end = mid - 1;
    }
  }

  document.body.removeChild(tempElement);

  // Trim to last complete word to avoid cutting words in half
  const lastSpaceIndex = bestFit.lastIndexOf(' ');
  if (lastSpaceIndex > 0 && lastSpaceIndex > bestFit.length * 0.7) {
    bestFit = bestFit.substring(0, lastSpaceIndex);
  }

  return { needsTruncation: true, truncatedText: bestFit };
};
